import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/contexts/AuthContext";
import { AppShell3 } from "@/components/app-shell-3";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { QualificationsTable } from "@/components/QualificationsTable";
import { Input } from "@/components/ui/input";
import { PageTransition } from "@/components/PageTransition";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  MapPin,
  Briefcase,
  Calendar,
  Mail,
  Phone,
  Settings,
  Home,
  Activity,
  Award,
  Users,
  FolderOpen,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  FileText,
  QrCode,
  Download,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  MessageSquare,
  PhoneCall,
  Building2,
  ArrowLeft,
  MoreVertical,
  User,
  List,
  MoveLeft,
  MoveRight,
  Sparkles,
  ArrowDown,
  Upload,
  Search,
  Briefcase as BriefcaseIcon,
  Home as HomeIcon,
  GraduationCap,
  FileCheck,
  FilePlus,
  Trash2,
  Eye,
  Shield,
  HeartPulse,
  AlertTriangle,
  Star,
  LayoutGrid,
  CalendarDays,
  Boxes,
  Sun,
  Palmtree,
  CalendarCheck,
  ExternalLink,
  Heart,
  Globe,
  BarChart,
  Table,
  Database,
  Code,
  Wrench,
  Zap,
  Rocket,
  Target,
  RefreshCw,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { gcsUploadFile, gcsDeleteFile, gcsGetSignedReadUrl, gcsCopyFile } from "@/lib/gcsStorage";
import { parseAiFlowResponse } from "@/lib/parseAiFlowResponse";
import { QRCodeSVG } from 'qrcode.react';
import { toast } from "sonner";
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ProjectCard } from '@/components/ProjectCard';
import { AnimatePresence, motion } from 'framer-motion';
import { ProfileDocuments } from './components/ProfileDocuments';
import { BasicProfileView } from './components/BasicProfileView';
import { ProfileQualificationsManager } from './components/ProfileQualificationsManager';
import { SendSmsDialog } from './components/SendSmsDialog';

// Icon map for custom profile tabs (matches CustomProfileTabsManager)
const PROFILE_TAB_ICONS = {
  ExternalLink, Globe, BarChart, Table, Database, Code,
  Settings, Wrench, Zap, Rocket, Target, User, Briefcase,
  Shield, Heart, FileText, LayoutGrid,
};

/**
 * Resolve variables in a custom tab URL using profile and company data.
 * Replaces {user_profile.id}, {company.name}, etc. with actual values.
 */
function resolveTabUrl(urlTemplate, profile, companyData) {
  if (!urlTemplate) return '';
  return urlTemplate.replace(/\{([^}]+)\}/g, (match, key) => {
    const parts = key.split('.');
    if (parts[0] === 'user_profile' && profile) {
      return encodeURIComponent(profile[parts[1]] || '');
    }
    if (parts[0] === 'company' && companyData) {
      return encodeURIComponent(companyData[parts[1]] || '');
    }
    return match; // Leave unresolved vars as-is
  });
}

export function ProfilePage() {
  const { userId } = useParams(); // Optional - if present, viewing another user
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile: currentUserProfile, company: currentUserCompany, userRole, can } = useAuth();

  // State for the viewed profile (could be current user or another user)
  const [profileData, setProfileData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [teamMemberData, setTeamMemberData] = useState(null);
  const [isTeamMemberProfile, setIsTeamMemberProfile] = useState(false);

  // Determine if viewing own profile
  const isOwnProfile = !userId || userId === currentUserProfile?.id;

  // Determine if user is authorized to see full profile (Admin, Manager, or own profile)
  const isAuthorized = isOwnProfile || userRole === 'Admin' || userRole === 'Manager';

  // Tab state - read initial tab from URL search params
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  // Use the correct profile based on context
  const userProfile = isOwnProfile ? currentUserProfile : profileData;
  const company = isOwnProfile ? currentUserCompany : companyData;

  // Set page title based on whose profile is being viewed
  const displayName = userProfile?.first_name && userProfile?.last_name
    ? `${userProfile.first_name} ${userProfile.last_name}`
    : userProfile?.first_name || 'Profile';
  usePageTitle(isOwnProfile ? 'My Profile' : displayName);

  const [stats, setStats] = useState({
    projectsCount: 0,
    tasksCompleted: 0,
    teamMembers: 0,
    joinedDate: null
  });
  const [loading, setLoading] = useState(true);
  const [qualificationsData, setQualificationsData] = useState([]);
  const [qualificationsLoading, setQualificationsLoading] = useState(true);
  const [assignedRequirementsData, setAssignedRequirementsData] = useState(null); // Aggregated stats from RPC for header
  const [userSpaces, setUserSpaces] = useState([]);
  const [spacesPage, setSpacesPage] = useState(1);
  const spacesScrollRef = useRef(null);
  const [checkInStatus, setCheckInStatus] = useState(null); // Currently checked in location
  const [leaveStatus, setLeaveStatus] = useState(null); // Upcoming/current leave
  const [upcomingLeave, setUpcomingLeave] = useState([]); // Upcoming approved leave
  const [leaveBalance, setLeaveBalance] = useState(null); // Leave entitlement balance for current year
  const [leaveHistory, setLeaveHistory] = useState([]); // All leave requests for history
  const [leaveLoading, setLeaveLoading] = useState(true); // Loading state for leave
  const [leaveViewMode, setLeaveViewMode] = useState('list'); // 'list' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth()); // Calendar month (0-11)
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear()); // Calendar year
  const [recentPosts, setRecentPosts] = useState([]); // Recent message board posts
  const [postsLoading, setPostsLoading] = useState(true); // Loading state for posts
  const [showQRDialog, setShowQRDialog] = useState(false); // QR code dialog
  const [showEmergencyContactDialog, setShowEmergencyContactDialog] = useState(false); // Emergency contact dialog
  const [showSmsDialog, setShowSmsDialog] = useState(false); // Send SMS dialog
  const [spacesSearchQuery, setSpacesSearchQuery] = useState(''); // Search filter for spaces
  const [userStacks, setUserStacks] = useState([]); // User's stacks for determining message board links
  const [appliedRoleTemplates, setAppliedRoleTemplates] = useState([]);

  // Custom profile tabs from company settings
  const [customTabLoadingStates, setCustomTabLoadingStates] = useState({});
  const customProfileTabs = useMemo(() => {
    const tabs = company?.settings?.custom_profile_tabs;
    if (!Array.isArray(tabs)) return [];
    return tabs.filter(t => t.enabled);
  }, [company?.settings?.custom_profile_tabs]);

  // Schedule state - cache multiple weeks for instant navigation
  const [scheduleCache, setScheduleCache] = useState(new Map());
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    // Get Monday of current week
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(today.setDate(diff));
  });

  // Get schedule data for current week from cache
  const scheduleData = useMemo(() => {
    const weekKey = currentWeekStart.toISOString().split('T')[0];
    return scheduleCache.get(weekKey) || null;
  }, [scheduleCache, currentWeekStart]);

  // Smart upload state for drag-and-drop
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [smartUploadProcessing, setSmartUploadProcessing] = useState(false);
  const [smartUploadConfirmation, setSmartUploadConfirmation] = useState(null);
  const [manualUploadConfirmation, setManualUploadConfirmation] = useState(null);
  const dragCounterRef = useRef(0);

  // Calculate qualification statistics from aggregated RPC stats (matches matrix calculation)
  const qualificationStats = useMemo(() => {
    // assignedRequirementsData is now an aggregated stats object from RPC, not an array
    const stats = assignedRequirementsData;

    // Handle null/empty case (initial state or error)
    if (!stats || !stats.assigned_count) {
      return { total: 0, compliant: 0, compliancePercentage: 0, quoting: 0, booked: 0, expiring: 0, expired: 0, missing: 0 };
    }

    const total = stats.assigned_count;
    const valid = stats.valid || 0;
    const expiring = stats.expiring || 0;
    const expired = stats.expired || 0;
    const missing = stats.missing || 0;
    const booked = stats.booked || 0;
    const quoting = stats.quoting || 0;

    // Compliance = valid + expiring
    const compliant = valid + expiring;
    const compliancePercentage = total > 0 ? Math.round((compliant / total) * 100) : 0;

    return {
      total,
      compliant,
      compliancePercentage,
      quoting,
      booked,
      expiring,
      expired,
      missing
    };
  }, [assignedRequirementsData]);

  // Load profile data when viewing another user
  useEffect(() => {
    const loadProfile = async () => {
      if (isOwnProfile) {
        // Viewing own profile - data comes from AuthContext
        setProfileData(currentUserProfile);
        setCompanyData(currentUserCompany);
        setLoading(false);
        return;
      }

      if (!userId || !currentUserCompany?.id) return;

      try {
        setLoading(true);

        // SECURITY: First verify the user being viewed is in the same company
        const { data: membershipCheck, error: membershipError } = await supabase
          .from('company_members')
          .select('id')
          .eq('user_id', userId)
          .eq('company_id', currentUserCompany.id)
          .single();

        if (membershipError || !membershipCheck) {
          // User is not in the same company - this is a security violation attempt
          console.error('Security: Attempted to view profile from different company');
          toast.error('You do not have permission to view this profile');
          navigate('/company-team');
          return;
        }

        // Try to load as user_profile first
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError) {
          toast.error('Profile not found');
          navigate('/company-team');
          return;
        }

        setProfileData(profile);

        // Load company data for this user (we already verified they're in our company)
        setCompanyData(currentUserCompany);
      } catch (error) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId, isOwnProfile, currentUserProfile, currentUserCompany, navigate]);

  // Load applied role templates for this user
  useEffect(() => {
    const loadRoleTemplates = async () => {
      if (!userProfile?.id) return;

      const { data: links } = await supabase
        .from('user_role_templates')
        .select('role_template_id')
        .eq('user_id', userProfile.id);

      if (links && links.length > 0) {
        const { data: templates } = await supabase
          .from('role_templates')
          .select('id, name, color, job_profile')
          .in('id', links.map(l => l.role_template_id))
          .eq('is_active', true);

        setAppliedRoleTemplates(templates || []);
      } else {
        setAppliedRoleTemplates([]);
      }
    };

    loadRoleTemplates();
  }, [userProfile?.id]);

  useEffect(() => {
    const loadUserStats = async () => {
      if (!userProfile?.id) return;

      try {
        // Load spaces for user from space_members
        const { data: spaces, error: spacesError } = await supabase
          .from('space_members')
          .select(`
            id,
            role,
            created_date,
            project:project_id (
              id,
              name,
              description,
              type,
              location,
              avatar,
              color,
              tools
            )
          `)
          .eq('user_id', userProfile.id);

        const filteredSpaces = (spaces || []).filter(s => s.project);

        // Fetch team members for all projects
        if (filteredSpaces.length > 0) {
          const projectIds = filteredSpaces.map(s => s.project.id);

          const { data: allTeamMembers, error: teamMembersError } = await supabase
            .from('space_members')
            .select(`
              project_id,
              user_profiles!user_id (
                id,
                first_name,
                last_name,
                avatar_url
              )
            `)
            .in('project_id', projectIds);

          // Group team members by project_id
          const teamMembersByProject = {};
          allTeamMembers?.forEach(member => {
            if (!teamMembersByProject[member.project_id]) {
              teamMembersByProject[member.project_id] = [];
            }
            if (member.user_profiles) {
              const firstName = member.user_profiles.first_name || '';
              const lastName = member.user_profiles.last_name || '';
              const fullName = `${firstName} ${lastName}`.trim();

              teamMembersByProject[member.project_id].push({
                id: member.user_profiles.id,
                name: fullName,
                first_name: firstName,
                last_name: lastName,
                avatar: member.user_profiles.avatar_url
              });
            }
          });

          // Attach team members to each space
          filteredSpaces.forEach(space => {
            space.project.teamMembers = teamMembersByProject[space.project.id] || [];
            // Initialize with empty compliance summary - loaded lazily below
            space.project.complianceSummary = null;
          });
        }

        setUserSpaces(filteredSpaces);

        setStats({
          projectsCount: filteredSpaces.length,
          tasksCompleted: 0,
          teamMembers: 0,
          joinedDate: userProfile.employment_start_date || userProfile.created_date
        });
      } catch (error) {
        console.error('Error loading user stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserStats();
  }, [userProfile?.id, company?.id]);

  // Lazy-load compliance stats for spaces (deferred - doesn't block page render)
  useEffect(() => {
    if (!userSpaces.length) return;
    const projectIds = userSpaces.map(s => s.project?.id).filter(Boolean);
    if (!projectIds.length) return;

    const loadComplianceStats = async () => {
      const compliancePromises = projectIds.map(async (projectId) => {
        try {
          const { data: stats, error: statsError } = await supabase.rpc(
            'calculate_compliance_stats',
            { p_project_id: projectId }
          );
          if (statsError) return { projectId, stats: null };
          return { projectId, stats };
        } catch {
          return { projectId, stats: null };
        }
      });

      const complianceResults = await Promise.all(compliancePromises);

      const complianceByProject = {};
      complianceResults.forEach(({ projectId, stats }) => {
        if (stats?.overall) {
          complianceByProject[projectId] = {
            valid: (stats.overall.valid || 0) + (stats.overall.valid_cross_space || 0),
            expiring: (stats.overall.expiring || 0) + (stats.overall.expiring_cross_space || 0),
            expired: stats.overall.expired || 0,
            missing: stats.overall.missing || 0,
            total_required: stats.overall.total_cells || 0,
            percentage: Math.round(stats.overall.compliance_percentage || 0)
          };
        }
      });

      // Update spaces with compliance data (immutable update to trigger re-render)
      setUserSpaces(prev => prev.map(space => ({
        ...space,
        project: space.project ? {
          ...space.project,
          complianceSummary: complianceByProject[space.project.id] || {
            valid: 0, expiring: 0, expired: 0, missing: 0, total_required: 0, percentage: 0
          }
        } : space.project
      })));
    };

    loadComplianceStats();
  }, [userSpaces.length]);

  // Load check-in status and leave status
  useEffect(() => {
    const loadAdditionalData = async () => {
      if (!userProfile?.id) return;

      try {
        // Check for active check-in
        // Run check-in and leave status queries in parallel
        const today = new Date().toISOString().split('T')[0];
        const [checkInResult, leaveResult] = await Promise.all([
          supabase
            .from('site_checkins')
            .select(`
              *,
              site_location:site_location_id ( name ),
              project:project_id ( name )
            `)
            .eq('user_id', userProfile.id)
            .is('check_out_time', null)
            .order('check_in_time', { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('leave_requests')
            .select('*')
            .eq('user_id', userProfile.id)
            .eq('status', 'approved')
            .gte('end_date', today)
            .order('start_date', { ascending: true })
            .limit(1)
            .maybeSingle()
        ]);

        const activeCheckIn = checkInResult.data;
        if (activeCheckIn) {
          setCheckInStatus({
            location: activeCheckIn.site_location?.name || 'Unknown Location',
            project: activeCheckIn.project?.name || 'Unknown Project',
            since: activeCheckIn.check_in_time
          });
        }

        const upcomingLeave = leaveResult.data;
        if (upcomingLeave) {
          const startDate = new Date(upcomingLeave.start_date);
          const endDate = new Date(upcomingLeave.end_date);
          const isCurrentlyOnLeave = startDate <= new Date() && endDate >= new Date();

          setLeaveStatus({
            type: upcomingLeave.leave_type,
            startDate: upcomingLeave.start_date,
            endDate: upcomingLeave.end_date,
            isCurrent: isCurrentlyOnLeave
          });
        }
      } catch (error) {
        console.error('Error loading additional profile data:', error);
      }
    };

    if (userProfile?.id) {
      loadAdditionalData();
    }
  }, [userProfile?.id]);

  // Load qualifications from user_qualifications table (user-level, not space-specific)
  const loadQualifications = async (userProfileId) => {
    setQualificationsLoading(true);
    try {
      if (userProfileId) {
        // Query user_qualifications directly
        const { data: qualifications, error } = await supabase
          .from('user_qualifications')
          .select(`
            *,
            compliance_requirements (
              id,
              name,
              description,
              category,
              renewal_period
            )
          `)
          .eq('user_id', userProfileId)
          .order('created_date', { ascending: false });

        if (error) {
          console.error('Error fetching qualifications:', error);
          setQualificationsData([]);
        } else {
          // Get user's space memberships to find which spaces they're in
          const { data: spaceMembers } = await supabase
            .from('space_members')
            .select('user_id, project_id, projects(id, name, location, description, type, avatar, color)')
            .eq('user_id', userProfileId);

          // Get all requirement IDs from qualifications
          const requirementIds = qualifications?.map(q => q.requirement_id) || [];

          // Get compliance_assignments with project_id to correctly map requirements to spaces
          const { data: complianceAssigns } = await supabase
            .from('compliance_assignments')
            .select('requirement_id, team_member_id, project_id')
            .eq('team_member_id', userProfileId)
            .in('requirement_id', requirementIds);

          // Create a map of requirement_id to spaces
          const requirementToSpaces = new Map();

          // Process compliance_assignments - match to correct space by project_id
          complianceAssigns?.forEach(assign => {
            // Find the space that matches this assignment's project_id
            const spaceMember = spaceMembers?.find(sm => sm.project_id === assign.project_id);
            if (spaceMember?.projects) {
              if (!requirementToSpaces.has(assign.requirement_id)) {
                requirementToSpaces.set(assign.requirement_id, []);
              }
              const spaces = requirementToSpaces.get(assign.requirement_id);
              // Avoid duplicates
              if (!spaces.some(s => s.id === spaceMember.projects.id)) {
                spaces.push({
                  id: spaceMember.projects.id,
                  name: spaceMember.projects.name,
                  location: spaceMember.projects.location
                });
              }
            }
          });

          // Transform data to match expected format, including spaces
          const transformedData = qualifications?.map(qual => ({
            ...qual,
            requirement_name: qual.compliance_requirements?.name,
            requirement_description: qual.compliance_requirements?.description,
            requirement_category: qual.compliance_requirements?.category,
            requirement_id: qual.requirement_id,
            // Add spaces where this requirement is assigned
            spaces: requirementToSpaces.get(qual.requirement_id) || [],
            project_name: null,
            project_location: null,
            project_id: null,
            // Add user name for validation
            user_name: userProfile?.display_name || `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim()
          })) || [];

          setQualificationsData(transformedData);
        }
      }
    } catch (error) {
      console.error('Error loading qualifications:', error);
      setQualificationsData([]);
    } finally {
      setQualificationsLoading(false);
    }
  };

  // Load user's qualifications and assigned requirements together (shared queries)
  useEffect(() => {
    if (!userProfile?.id || !company?.id) return;

    const loadQualificationsAndRequirements = async () => {
      setQualificationsLoading(true);
      try {
        // Fetch shared data in parallel: qualifications, space memberships, and company space IDs
        const [qualsResult, spaceMembersResult] = await Promise.all([
          supabase
            .from('user_qualifications')
            .select(`
              *,
              compliance_requirements (
                id, name, description, category, renewal_period
              )
            `)
            .eq('user_id', userProfile.id)
            .order('created_date', { ascending: false }),
          supabase
            .from('space_members')
            .select('user_id, project_id, projects(id, name, location, description, type, avatar, color, company_id)')
            .eq('user_id', userProfile.id)
        ]);

        const qualifications = qualsResult.data || [];
        const spaceMembers = spaceMembersResult.data || [];

        // Filter to current company's spaces
        const companySpaceIds = spaceMembers
          .filter(sm => sm.projects?.company_id === company.id)
          .map(sm => sm.project_id);

        // Get requirement IDs from qualifications
        const requirementIds = [...new Set(qualifications.map(q => q.requirement_id).filter(Boolean))];

        // Fetch compliance_assignments once for both qualifications and stats
        const { data: complianceAssigns } = requirementIds.length > 0 || companySpaceIds.length > 0
          ? await supabase
              .from('compliance_assignments')
              .select('id, requirement_id, team_member_id, project_id, status, expiry_date, completed_date, assigned')
              .eq('team_member_id', userProfile.id)
              .in('project_id', companySpaceIds.length > 0 ? companySpaceIds : ['__none__'])
          : { data: [] };

        // --- Build qualifications data ---
        const requirementToSpaces = new Map();
        complianceAssigns?.forEach(assign => {
          const spaceMember = spaceMembers.find(sm => sm.project_id === assign.project_id);
          if (spaceMember?.projects) {
            if (!requirementToSpaces.has(assign.requirement_id)) {
              requirementToSpaces.set(assign.requirement_id, []);
            }
            const spaces = requirementToSpaces.get(assign.requirement_id);
            if (!spaces.some(s => s.id === spaceMember.projects.id)) {
              spaces.push({
                id: spaceMember.projects.id,
                name: spaceMember.projects.name,
                location: spaceMember.projects.location
              });
            }
          }
        });

        const transformedData = qualifications.map(qual => ({
          ...qual,
          requirement_name: qual.compliance_requirements?.name,
          requirement_description: qual.compliance_requirements?.description,
          requirement_category: qual.compliance_requirements?.category,
          requirement_id: qual.requirement_id,
          spaces: requirementToSpaces.get(qual.requirement_id) || [],
          project_name: null,
          project_location: null,
          project_id: null,
          user_name: userProfile?.display_name || `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim()
        }));

        setQualificationsData(transformedData);

        // --- Build assigned requirements stats ---
        if (companySpaceIds.length > 0) {
          const assignedOnly = (complianceAssigns || []).filter(a => a.assigned);

          // Create lookup for user qualifications
          const qualsMap = new Map();
          qualifications.filter(q => q.status === 'completed').forEach(q => {
            qualsMap.set(q.requirement_id, q);
          });

          const now = new Date();
          const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
          const stats = { assigned_count: 0, valid: 0, expiring: 0, expired: 0, missing: 0, booked: 0, quoting: 0 };
          const processedRequirements = new Set();

          assignedOnly.forEach(assignment => {
            if (processedRequirements.has(assignment.requirement_id)) return;
            processedRequirements.add(assignment.requirement_id);
            stats.assigned_count++;

            const qual = qualsMap.get(assignment.requirement_id);
            const effectiveExpiry = qual?.expiry_date || assignment.expiry_date;
            const isCompleted = qual?.status === 'completed' || assignment.completed_date;

            if (isCompleted && effectiveExpiry) {
              const expiryDate = new Date(effectiveExpiry);
              if (expiryDate <= now) stats.expired++;
              else if (expiryDate <= thirtyDaysFromNow) stats.expiring++;
              else stats.valid++;
            } else if (isCompleted) {
              stats.valid++;
            } else {
              stats.missing++;
            }

            const renewalStatus = assignment.status;
            if (renewalStatus === 'booked' || renewalStatus === 'in_progress') stats.booked++;
            else if (renewalStatus === 'quoting' || renewalStatus === 'quoted') stats.quoting++;
          });

          setAssignedRequirementsData(stats);
        } else {
          setAssignedRequirementsData(null);
        }
      } catch (error) {
        console.error('Error loading qualifications:', error);
        setQualificationsData([]);
        setAssignedRequirementsData(null);
      } finally {
        setQualificationsLoading(false);
      }
    };

    loadQualificationsAndRequirements();
  }, [userProfile?.id, company?.id]);

  // Load user documents (contracts, sickness, disciplinary, etc.)
  const loadDocuments = useCallback(async (userProfileId) => {
    if (!isAuthorized) {
      setDocumentsLoading(false);
      return;
    }

    setDocumentsLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_documents')
        .select(`
          *,
          uploader:uploaded_by(id, first_name, last_name, avatar_url)
        `)
        .eq('user_id', userProfileId)
        .order('created_date', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        setDocuments([]);
      } else {
        setDocuments(data || []);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  }, [isAuthorized]);

  // Load documents when profile is available and user is authorized
  useEffect(() => {
    if (userProfile?.id && isAuthorized) {
      loadDocuments(userProfile.id);
    }
  }, [userProfile?.id, isAuthorized, loadDocuments]);

  // Smart upload - automatically match certificate to correct qualification
  const handleSmartUpload = async (file) => {
    if (!userProfile?.id) {
      toast.error('User profile not loaded');
      return;
    }

    setSmartUploadProcessing(true);
    const loadingToast = toast.loading('AI is analyzing certificate...');

    try {
      // Get fresh qualifications data
      if (qualificationsData.length === 0) {
        toast.error('No qualifications found for this user', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Upload file to GCS temp folder
      let profileUploadResult;
      try {
        profileUploadResult = await gcsUploadFile(file, {
          folderPath: 'certificates/temp',
          fileName: `${Date.now()}_${file.name}`,
          contentType: file.type,
        });
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        toast.error('Failed to upload file', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Get signed URL for doc parser
      let profileSignedUrl;
      try {
        profileSignedUrl = await gcsGetSignedReadUrl(profileUploadResult.filePath);
      } catch {
        toast.error('Failed to process file', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Call doc parser webhook
      const response = await fetch('https://addai-flows-backend-29522465016.europe-west2.run.app/webhook/e2a909a7-6f57-461d-83b8-b1506cf3a79a/process-certificate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentUrl: profileSignedUrl,
          fileName: file.name,
          uploadedAt: new Date().toISOString()
        })
      });

      if (!response.ok) {
        toast.error('Failed to parse certificate', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      const responseData = await response.json();
      const parsed = parseAiFlowResponse(responseData);

      if (!parsed.success || !parsed.extractedData) {
        toast.error('Could not extract information from certificate', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      const extractedData = parsed.extractedData;

      // Extract course titles
      const courseTitles = Array.isArray(extractedData.course_title)
        ? extractedData.course_title
        : [extractedData.course_title];
      const expiryDate = Array.isArray(extractedData.expiry_date)
        ? extractedData.expiry_date[0]
        : extractedData.expiry_date;
      const certNumber = Array.isArray(extractedData.certificate_number)
        ? extractedData.certificate_number[0]
        : extractedData.certificate_number;

      console.log('Extracted data:', { courseTitles, expiryDate, certNumber });
      console.log('Trying to match against qualifications:', qualificationsData.map(q => q.requirement_name));

      if (!courseTitles.length) {
        toast.error('Could not find course title in certificate', { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Try to match qualification with all course title variations
      let matchedQualification = null;
      let matchedCourseTitle = '';

      for (const courseTitle of courseTitles) {
        const found = qualificationsData.find(qual => {
          const qualName = qual.requirement_name.toLowerCase();
          const courseLower = courseTitle.toLowerCase();

          // Remove common words that might differ
          const normalizeText = (text) => text
            .replace(/\b(training|course|certification|certificate)\b/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

          const normalizedQual = normalizeText(qualName);
          const normalizedCourse = normalizeText(courseLower);

          // Try exact match
          if (qualName === courseLower) return true;

          // Try contains match (either direction)
          if (qualName.includes(courseLower) || courseLower.includes(qualName)) return true;

          // Try normalized match
          if (normalizedQual.includes(normalizedCourse) || normalizedCourse.includes(normalizedQual)) return true;

          return false;
        });

        if (found) {
          matchedQualification = found;
          matchedCourseTitle = courseTitle;
          console.log(`✓ Matched qualification: ${found.requirement_name} using ${courseTitle}`);
          break;
        }
      }

      if (!matchedQualification) {
        toast.error(`Could not find qualification: ${courseTitles.join(' or ')}`, { id: loadingToast });
        setSmartUploadProcessing(false);
        return;
      }

      // Show confirmation dialog
      toast.success(`Matched: ${matchedQualification.requirement_name}`, { id: loadingToast });

      setSmartUploadConfirmation({
        qualification: matchedQualification,
        expiryDate: expiryDate,
        certificateNumber: certNumber,
        tempFilePath: profileUploadResult.filePath,
        fileName: file.name
      });

    } catch (error) {
      console.error('Smart upload error:', error);
      toast.error('Failed to process certificate', { id: loadingToast });
    } finally {
      setSmartUploadProcessing(false);
    }
  };

  // Confirm and save the smart upload
  const handleConfirmSmartUpload = async () => {
    if (!smartUploadConfirmation) return;

    const { qualification, expiryDate, certificateNumber, tempFilePath, fileName } = smartUploadConfirmation;
    const savingToast = toast.loading('Saving certificate...');

    try {
      // Move file to proper location via server-side copy
      const properFileName = `certificates/${userProfile.id}/${qualification.requirement_id}/${Date.now()}_${fileName}`;

      let finalPath;
      try {
        const copyResult = await gcsCopyFile(tempFilePath, properFileName);
        finalPath = copyResult.filePath;
        await gcsDeleteFile(tempFilePath);
      } catch (copyError) {
        console.warn('Copy failed, using temp path:', copyError);
        finalPath = tempFilePath;
      }

      // Update user_qualifications
      const singleFileEntry = [{
        url: finalPath,
        file_name: fileName,
        file_type: 'unknown',
        uploaded_at: new Date().toISOString(),
        uploaded_by: userProfile.id,
        is_primary: true
      }];

      const { error: updateError } = await supabase
        .from('user_qualifications')
        .update({
          status: 'completed',
          completed_date: new Date().toISOString(),
          expiry_date: expiryDate || null,
          evidence_url: finalPath,
          evidence_files: singleFileEntry,
          certificate_number: certificateNumber || null,
          updated_date: new Date().toISOString()
        })
        .eq('user_id', userProfile.id)
        .eq('requirement_id', qualification.requirement_id);

      if (updateError) throw updateError;

      toast.success('Certificate uploaded successfully!', { id: savingToast });
      await loadQualifications(userProfile.id);
      setSmartUploadConfirmation(null);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save certificate', { id: savingToast });
    }
  };

  // Decline the smart upload and clean up temp file
  const handleDeclineSmartUpload = async () => {
    if (!smartUploadConfirmation) return;

    try {
      // Delete temp file from GCS
      await gcsDeleteFile(smartUploadConfirmation.tempFilePath);

      toast.info('Certificate upload cancelled');
      setSmartUploadConfirmation(null);
    } catch (error) {
      console.error('Cleanup error:', error);
      setSmartUploadConfirmation(null);
    }
  };

  // Handle manual upload confirmation from the upload button
  const handleManualUploadConfirm = (uploadData) => {
    setManualUploadConfirmation(uploadData);
  };

  // Confirm and save manual upload
  const handleConfirmManualUpload = async () => {
    if (!manualUploadConfirmation) return;

    const { qualification, tempFilePath, file, extractedData } = manualUploadConfirmation;
    const savingToast = toast.loading('Saving certificate...');

    try {
      // Move file to proper location via server-side copy
      const userId = qualification.user_id || qualification.team_member_id;
      const properFileName = `certificates/${userId}/${qualification.requirement_id}/${Date.now()}_${file.name}`;

      let finalPath;
      try {
        const copyResult = await gcsCopyFile(tempFilePath, properFileName);
        finalPath = copyResult.filePath;
        await gcsDeleteFile(tempFilePath);
      } catch (copyError) {
        console.warn('Copy failed, using temp path:', copyError);
        finalPath = tempFilePath;
      }

      // Build evidence_files entry for forward compat
      const singleFileEntry = [{
        url: finalPath,
        file_name: file.name,
        file_type: file.type || 'unknown',
        uploaded_at: new Date().toISOString(),
        uploaded_by: qualification.user_id || null,
        is_primary: true
      }];

      // Update the correct table based on data structure
      if (qualification.user_id) {
        // Profile page: Update user_qualifications
        const { error: updateError } = await supabase
          .from('user_qualifications')
          .update({
            evidence_url: finalPath,
            evidence_files: singleFileEntry,
            notes: `Certificate uploaded: ${file.name}`,
            status: 'completed',
            completed_date: new Date().toISOString(),
            expiry_date: extractedData.expiryDate || null,
            certificate_number: extractedData.certificateNumber || null,
            updated_date: new Date().toISOString()
          })
          .eq('user_id', qualification.user_id)
          .eq('requirement_id', qualification.requirement_id);

        if (updateError) throw updateError;
      } else {
        // Matrix page: Update compliance_assignments
        const { error: updateError } = await supabase
          .from('compliance_assignments')
          .update({
            evidence_url: finalPath,
            notes: `Certificate uploaded: ${file.name}`,
            status: 'completed',
            completed_date: new Date().toISOString(),
            expiry_date: extractedData.expiryDate || null,
            certificate_number: extractedData.certificateNumber || null
          })
          .eq('id', qualification.id);

        if (updateError) throw updateError;
      }

      toast.success('Certificate uploaded successfully!', { id: savingToast });
      await loadQualifications(userProfile.id);
      setManualUploadConfirmation(null);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save certificate', { id: savingToast });
    }
  };

  // Decline manual upload
  const handleDeclineManualUpload = async () => {
    if (!manualUploadConfirmation) return;

    try {
      // Delete temp file from GCS
      await gcsDeleteFile(manualUploadConfirmation.tempFilePath);

      toast.info('Certificate upload cancelled');
      setManualUploadConfirmation(null);
    } catch (error) {
      console.error('Cleanup error:', error);
      setManualUploadConfirmation(null);
    }
  };

  // Drag-and-drop listeners
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (e.dataTransfer.types.includes('Files')) {
        setIsDraggingFiles(true);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDraggingFiles(false);
      }
    };

    const handleDrop = async (e) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDraggingFiles(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];

        // Validate file type
        const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
          toast.error('Please upload a PDF, JPG, or PNG file');
          return;
        }

        // Use smart upload to automatically match certificate
        await handleSmartUpload(file);
      }
    };

    document.body.addEventListener('dragenter', handleDragEnter);
    document.body.addEventListener('dragover', handleDragOver);
    document.body.addEventListener('dragleave', handleDragLeave);
    document.body.addEventListener('drop', handleDrop);

    return () => {
      document.body.removeEventListener('dragenter', handleDragEnter);
      document.body.removeEventListener('dragover', handleDragOver);
      document.body.removeEventListener('dragleave', handleDragLeave);
      document.body.removeEventListener('drop', handleDrop);
    };
  }, [qualificationsData, userProfile?.id]);

  // Load leave data: upcoming, balance, and history
  useEffect(() => {
    const fetchLeaveData = async () => {
      if (!userProfile?.id) {
        return;
      }

      try {
        setLeaveLoading(true);
        const today = new Date().toISOString().split('T')[0];
        const currentYear = new Date().getFullYear();

        // Fetch upcoming approved leave, leave balance, and full history in parallel
        const [upcomingResult, balanceResult, historyResult] = await Promise.all([
          supabase
            .from('leave_requests')
            .select('*')
            .eq('user_id', userProfile.id)
            .eq('status', 'approved')
            .gte('end_date', today)
            .order('start_date', { ascending: true }),
          supabase
            .from('leave_balances')
            .select('*')
            .eq('user_id', userProfile.id)
            .eq('year', currentYear)
            .single(),
          supabase
            .from('leave_requests')
            .select('*')
            .eq('user_id', userProfile.id)
            .order('start_date', { ascending: false })
        ]);

        if (upcomingResult.error) throw upcomingResult.error;

        setUpcomingLeave(upcomingResult.data || []);
        setLeaveBalance(balanceResult.data || null);
        setLeaveHistory(historyResult.data || []);
      } catch (error) {
        console.error('Error fetching leave data:', error);
        setUpcomingLeave([]);
        setLeaveBalance(null);
        setLeaveHistory([]);
      } finally {
        setLeaveLoading(false);
      }
    };

    fetchLeaveData();
  }, [userProfile?.id]);

  // Load recent posts
  useEffect(() => {
    const fetchRecentPosts = async () => {
      if (!userProfile?.id || !currentUserProfile?.id) {
        console.log('No userProfile or currentUserProfile, skipping posts fetch');
        return;
      }

      try {
        setPostsLoading(true);

        // First, get all projects the current logged-in user has access to
        const { data: userProjects, error: projectsError } = await supabase
          .from('space_members')
          .select('project_id')
          .eq('user_id', currentUserProfile.id);

        if (projectsError) throw projectsError;

        const accessibleProjectIds = userProjects?.map(p => p.project_id) || [];
        console.log('User accessible project IDs:', accessibleProjectIds);
        console.log('Fetching posts for user_id:', userProfile.id);

        // Get posts created by the viewed user
        const { data: allPosts, error: postsError } = await supabase
          .from('message_board_posts')
          .select(`
            *,
            projects:project_id(name),
            company:company_id(name)
          `)
          .eq('created_by', userProfile.id)
          .order('created_date', { ascending: false })
          .limit(20);

        if (postsError) {
          console.error('Error fetching posts:', postsError);
          throw postsError;
        }

        console.log('Raw posts from database:', allPosts);
        console.log('Number of posts returned:', allPosts?.length || 0);

        // Filter posts based on access rules
        const filteredPosts = (allPosts || []).filter(post => {
          console.log(`Checking post "${post.title}":`, {
            project_id: post.project_id,
            company_id: post.company_id,
            selected_project_ids: post.selected_project_ids,
            hasAccess: accessibleProjectIds.includes(post.project_id),
            sameCompany: currentUserProfile.company_id === post.company_id
          });

          // Rule 1: If has project_id and company_id → single project post
          if (post.project_id && post.company_id) {
            return accessibleProjectIds.includes(post.project_id);
          }

          // Rule 2: If only company_id (no project_id, empty selected_project_ids) → company-wide post
          if (post.company_id && !post.project_id && (!post.selected_project_ids || post.selected_project_ids.length === 0)) {
            // Check if viewer is in the same company
            return currentUserProfile.company_id === post.company_id;
          }

          // Rule 3: If has company_id and selected_project_ids → stack-wide post
          if (post.company_id && post.selected_project_ids && post.selected_project_ids.length > 0) {
            // Show if user has access to at least one of the selected projects
            return post.selected_project_ids.some(projectId => accessibleProjectIds.includes(projectId));
          }

          return false;
        }).slice(0, 3); // Limit to 3 most recent

        console.log('Filtered posts:', filteredPosts);
        console.log('Number of filtered posts:', filteredPosts.length);

        // Fetch project names for posts with multiple spaces (selected_project_ids)
        const postsWithMultipleSpaces = filteredPosts.filter(
          post => post.selected_project_ids && post.selected_project_ids.length > 0
        );

        if (postsWithMultipleSpaces.length > 0) {
          // Get all unique project IDs
          const allProjectIds = [...new Set(
            postsWithMultipleSpaces.flatMap(post => post.selected_project_ids)
          )];

          // Fetch project names
          const { data: projectsData } = await supabase
            .from('projects')
            .select('id, name')
            .in('id', allProjectIds);

          // Create a map of project ID to name
          const projectNameMap = {};
          projectsData?.forEach(proj => {
            projectNameMap[proj.id] = proj.name;
          });

          // Attach project names to posts
          filteredPosts.forEach(post => {
            if (post.selected_project_ids && post.selected_project_ids.length > 0) {
              post.selectedProjectNames = post.selected_project_ids
                .map(id => projectNameMap[id])
                .filter(Boolean);
            }
          });
        }

        setRecentPosts(filteredPosts);
      } catch (error) {
        console.error('Error fetching recent posts:', error);
        setRecentPosts([]);
      } finally {
        setPostsLoading(false);
      }
    };

    fetchRecentPosts();
  }, [userProfile?.id, currentUserProfile?.id, currentUserProfile?.company_id]);

  // Load current user's stacks for smart message board navigation
  useEffect(() => {
    const fetchUserStacks = async () => {
      if (!currentUserProfile?.id || !company?.id) return;

      try {
        const { data, error } = await supabase
          .from('user_dashboard_settings')
          .select('stacks')
          .eq('user_id', currentUserProfile.id)
          .eq('company_id', company.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error loading stacks:', error);
          return;
        }

        setUserStacks(data?.stacks || []);
      } catch (error) {
        console.error('Error loading stacks:', error);
      }
    };

    fetchUserStacks();
  }, [currentUserProfile?.id, company?.id]);

  // Schedule data loading disabled - Schedule & Training section is currently commented out in the UI
  // Re-enable this useEffect when the section is restored
  // (was loading 3 Supabase queries: shift_template_assignments, shift_patterns, att_requests)

  // Helper functions for schedule
  const getLeaveTypeLabel = (typeId) => {
    const leaveTypes = {
      100001: 'Annual Leave',
      100002: 'Sick Leave',
      100003: 'Unpaid Leave',
      100004: 'Work From Home',
      100005: 'Compassionate Leave',
      100006: 'Parental Leave',
      100007: 'Training'
    };
    return leaveTypes[typeId] || 'Leave';
  };

  const getShiftIcon = (status) => {
    switch(status) {
      case 'on_site': return BriefcaseIcon;
      case 'remote': return HomeIcon;
      case 'training': return GraduationCap;
      default: return BriefcaseIcon;
    }
  };

  const formatWeekRange = (startDate) => {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);

    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return `${startDate.toLocaleDateString('en-GB', options)}`;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}` || '??';
  };

  const formatLeaveType = (typeName) => {
    const types = {
      'annual_leave': 'Annual Leave',
      'sick_leave': 'Sick Leave',
      'unpaid_leave': 'Unpaid Leave',
      'work_from_home': 'Work From Home',
      'compassionate_leave': 'Compassionate Leave',
      'parental_leave': 'Parental Leave',
      'training': 'Training'
    };
    return types[typeName] || typeName;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // Extract city from full address (e.g., "123 Street, London, UK" -> "London")
  const extractCity = (location) => {
    if (!location) return '';
    const parts = location.split(',').map(p => p.trim());
    // If there are multiple parts, take the second-to-last (usually the city)
    // Handle cases like "Street, City, Postcode, Country" or "Street, City, Country"
    if (parts.length >= 2) {
      // Return the second-to-last part (usually the city before country)
      return parts[parts.length - 2];
    }
    return location; // Return as-is if can't parse
  };

  const sendEmergencyContactReminder = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userProfile.id,
          type: 'system',
          title: 'Emergency Contact Required',
          message: `${currentUserProfile.first_name} ${currentUserProfile.last_name} has requested that you add your emergency contact details to your profile.`,
          link_url: '/settings',
          entity_type: 'profile',
          entity_id: userProfile.id,
          actor_id: currentUserProfile.id,
          group_key: `emergency_contact_reminder_${userProfile.id}`
        });

      if (error) throw error;

      toast.success(`Reminder sent to ${userProfile.first_name}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder');
    }
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('user-qr-code');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${getDisplayName()}-QR-Code.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const getUserInitials = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name.charAt(0)}${userProfile.last_name.charAt(0)}`.toUpperCase();
    }
    if (userProfile?.first_name) {
      return userProfile.first_name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'US';
  };

  const getDisplayName = () => {
    if (userProfile?.first_name && userProfile?.last_name) {
      return `${userProfile.first_name} ${userProfile.last_name}`;
    }
    if (userProfile?.first_name || userProfile?.last_name) {
      return userProfile.first_name || userProfile.last_name;
    }
    return user?.email?.split('@')[0] || 'User';
  };

  if (loading && !isOwnProfile) {
    return (
      <AppShell3>
        <PageTransition>
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner />
          </div>
        </PageTransition>
      </AppShell3>
    );
  }

  return (
    <AppShell3 backgroundColor="#f8f7f3">
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PageTransition>
            {/* Back Button (when viewing another user) */}
            {!isOwnProfile && (
              <div className="pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="mb-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </div>
            )}

            {/* Profile Header */}
            <div className="pt-8">
          <motion.div
            className="bg-white rounded-lg shadow-lg border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="p-6">
              <div className="flex items-start gap-6">
                {/* Profile Avatar - smaller */}
                <Avatar className="h-20 w-20 border-2 border-gray-200 flex-shrink-0">
                  <AvatarImage
                    src={userProfile?.avatar_url || user?.user_metadata?.avatar_url}
                    alt={getDisplayName()}
                  />
                  <AvatarFallback className="text-lg font-semibold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  {/* Name and Status Badges */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-gray-900">
                        {getDisplayName()}
                      </h1>

                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {/* Job Title Badge */}
                        <Badge variant="outline" className="bg-white">
                          <Briefcase className="h-3 w-3 mr-1" />
                          {userProfile?.job_title || 'Team Member'}
                        </Badge>

                        {/* Check-in Status Badge */}
                        {checkInStatus && (
                          <Badge className="bg-green-100 text-green-800 border-green-200">
                            <MapPin className="h-3 w-3 mr-1" />
                            Checked In at {checkInStatus.project}
                          </Badge>
                        )}

                        {/* Leave Status Badge */}
                        {leaveStatus && (
                          <Badge className={
                            leaveStatus.isCurrent
                              ? "bg-orange-100 text-orange-800 border-orange-200"
                              : "bg-blue-100 text-blue-800 border-blue-200"
                          }>
                            <Calendar className="h-3 w-3 mr-1" />
                            {leaveStatus.isCurrent ? 'On Leave' : 'Upcoming Leave'} - {getLeaveTypeLabel(leaveStatus.type)}
                          </Badge>
                        )}

                        {/* Applied Role Templates */}
                        {appliedRoleTemplates.map(tmpl => {
                          const badge = (
                            <Badge
                              variant="outline"
                              className={tmpl.job_profile ? "cursor-pointer" : ""}
                              style={{
                                backgroundColor: (tmpl.color || '#6366f1') + '15',
                                borderColor: (tmpl.color || '#6366f1') + '40',
                                color: tmpl.color || '#6366f1'
                              }}
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" />
                              {tmpl.name}
                            </Badge>
                          );
                          if (tmpl.job_profile) {
                            return (
                              <Popover key={tmpl.id}>
                                <PopoverTrigger asChild>
                                  {badge}
                                </PopoverTrigger>
                                <PopoverContent className="w-80 max-h-64 overflow-y-auto" align="start">
                                  <div className="space-y-2">
                                    <div className="font-medium text-sm">{tmpl.name} — Job Profile</div>
                                    <div
                                      className="prose prose-sm text-xs text-muted-foreground"
                                      dangerouslySetInnerHTML={{ __html: tmpl.job_profile }}
                                    />
                                  </div>
                                </PopoverContent>
                              </Popover>
                            );
                          }
                          return <span key={tmpl.id}>{badge}</span>;
                        })}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 ml-4">
                      {isOwnProfile ? (
                        <>
                          {can('sms.send') && userProfile?.phone_number && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowSmsDialog(true)}
                            >
                              <MessageSquare className="h-4 w-4" />
                              Send SMS
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowEmergencyContactDialog(true)}
                          >
                            <PhoneCall className="h-4 w-4" />
                            Emergency Contact
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/chat?userId=${userProfile.id}`)}
                          >
                            <MessageCircle className="h-4 w-4 scale-x-[-1]" />
                            Send Chat
                          </Button>
                          {can('sms.send') && userProfile?.phone_number && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowSmsDialog(true)}
                            >
                              <MessageSquare className="h-4 w-4" />
                              Send SMS
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowEmergencyContactDialog(true)}
                          >
                            <PhoneCall className="h-4 w-4" />
                            Emergency Contact
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Meta Information Icons Row */}
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-600">
                    {userProfile?.location && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span>{extractCity(userProfile.location)}</span>
                      </div>
                    )}
                    {company?.name && (
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span>{company.name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>Started {formatDate(stats.joinedDate)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span>{userProfile?.email || user?.email}</span>
                    </div>
                    {userProfile?.phone_number && (
                      <div className="flex items-center gap-1.5">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{userProfile.phone_number}</span>
                      </div>
                    )}
                  </div>

                  {/* Qualification Stats - Mini Cards */}
                  <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t">
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                      <FileText className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-xs font-medium text-gray-600">Requirements:</span>
                      <span className="text-xs font-bold text-gray-900">{qualificationStats.total}</span>
                    </div>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border ${
                      qualificationStats.compliancePercentage >= 80 ? 'bg-green-50 border-green-200' :
                      qualificationStats.compliancePercentage >= 50 ? 'bg-yellow-50 border-yellow-200' :
                      'bg-red-50 border-red-200'
                    }`}>
                      <CheckCircle className={`h-3.5 w-3.5 ${
                        qualificationStats.compliancePercentage >= 80 ? 'text-green-600' :
                        qualificationStats.compliancePercentage >= 50 ? 'text-yellow-600' :
                        'text-red-600'
                      }`} />
                      <span className={`text-xs font-medium ${
                        qualificationStats.compliancePercentage >= 80 ? 'text-green-700' :
                        qualificationStats.compliancePercentage >= 50 ? 'text-yellow-700' :
                        'text-red-700'
                      }`}>Compliance:</span>
                      <span className={`text-xs font-bold ${
                        qualificationStats.compliancePercentage >= 80 ? 'text-green-700' :
                        qualificationStats.compliancePercentage >= 50 ? 'text-yellow-700' :
                        'text-red-700'
                      }`}>{qualificationStats.compliancePercentage}%</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-50 rounded-md border border-green-200">
                      <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-xs font-medium text-green-700">Compliant:</span>
                      <span className="text-xs font-bold text-green-700">{qualificationStats.compliant}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-orange-50 rounded-md border border-orange-200">
                      <AlertCircle className="h-3.5 w-3.5 text-orange-600" />
                      <span className="text-xs font-medium text-orange-700">Missing:</span>
                      <span className="text-xs font-bold text-orange-700">{qualificationStats.missing}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-purple-50 rounded-md border border-purple-200">
                      <Clock className="h-3.5 w-3.5 text-purple-600" />
                      <span className="text-xs font-medium text-purple-700">Quoting:</span>
                      <span className="text-xs font-bold text-purple-700">{qualificationStats.quoting}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 rounded-md border border-blue-200">
                      <CheckCircle className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-xs font-medium text-blue-700">Booked:</span>
                      <span className="text-xs font-bold text-blue-700">{qualificationStats.booked}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-yellow-50 rounded-md border border-yellow-200">
                      <AlertCircle className="h-3.5 w-3.5 text-yellow-600" />
                      <span className="text-xs font-medium text-yellow-700">Expiring:</span>
                      <span className="text-xs font-bold text-yellow-700">{qualificationStats.expiring}</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 rounded-md border border-red-200">
                      <XCircle className="h-3.5 w-3.5 text-red-600" />
                      <span className="text-xs font-medium text-red-700">Expired:</span>
                      <span className="text-xs font-bold text-red-700">{qualificationStats.expired}</span>
                    </div>
                  </div>
                </div>

                {/* Profile Menu - Right Side (Own Profile Only) */}
                {isOwnProfile && (
                  <div className="flex-shrink-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate('/settings')}>
                          <Settings className="h-4 w-4 mr-2" />
                          Edit Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowQRDialog(true)}>
                          <QrCode className="h-4 w-4 mr-2" />
                          Show QR Code
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Content Section - Conditional based on authorization */}
        {!isAuthorized ? (
          /* Basic profile view for unauthorized users */
          <BasicProfileView
            userProfile={userProfile}
            company={company}
            qualificationStats={qualificationStats}
            sharedSpaces={userSpaces.map(s => s.project).filter(Boolean)}
          />
        ) : (
          /* Full tabbed profile for authorized users (Admin, Manager, or own profile) */
          <div className="py-8">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              {/* Folder tabs */}
              <div>
                {/* Tabs row with bottom separator line */}
                <div className="relative flex items-stretch overflow-x-auto" style={{ marginBottom: '-1px' }}>
                  {[
                    { value: 'overview', label: 'Overview', icon: LayoutGrid },
                    { value: 'qualifications', label: 'Qualifications', icon: GraduationCap },
                    { value: 'documents', label: 'Documents', icon: FileText },
                    { value: 'spaces', label: 'Spaces', icon: Boxes },
                    { value: 'leave', label: 'Leave', icon: CalendarDays },
                    ...customProfileTabs.map(ct => ({
                      value: `custom_${ct.id}`,
                      label: ct.name,
                      icon: PROFILE_TAB_ICONS[ct.icon] || ExternalLink,
                      isCustom: true,
                      customColor: ct.color,
                    })),
                  ].map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.value;
                    return (
                      <button
                        key={tab.value}
                        onClick={() => setActiveTab(tab.value)}
                        className={`
                          flex items-center gap-1.5 text-[13px] cursor-pointer transition-colors
                          ${isActive
                            ? 'font-semibold text-gray-900'
                            : 'font-medium text-gray-500 hover:text-gray-700'
                          }
                        `}
                        style={isActive ? {
                          background: 'white',
                          borderTop: '1px solid #e5e7eb',
                          borderLeft: '1px solid #e5e7eb',
                          borderRight: '1px solid #e5e7eb',
                          borderBottom: '1px solid white',
                          borderRadius: '8px 8px 0 0',
                          padding: '10px 20px',
                          position: 'relative',
                          zIndex: 2,
                        } : {
                          background: 'transparent',
                          border: '1px solid transparent',
                          borderBottom: 'none',
                          padding: '10px 20px',
                          position: 'relative',
                          zIndex: 0,
                        }}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {/* Content - the folder body */}
                <div className="bg-white border border-gray-200 rounded-b-lg p-6" style={{ borderTopRightRadius: '8px' }}>
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0 p-0">
                {/* Two Column Layout - Upcoming Leave & Recent Posts */}
                <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 mb-6">
            {/* Upcoming Leave Section */}
            <Card className="shadow-sm overflow-hidden p-0 gap-0">
              <div className="py-2.5 px-3.5 border-b bg-gray-50/50 flex items-center justify-between h-11">
                <h3 className="text-sm font-semibold text-gray-900">Upcoming Leave</h3>
                <div className="flex items-center gap-1 bg-white rounded-md border border-gray-200 p-0.5">
                  <button
                    onClick={() => setLeaveViewMode('list')}
                    className={`p-1 rounded transition-colors ${
                      leaveViewMode === 'list'
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    title="List view"
                  >
                    <List className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setLeaveViewMode('calendar')}
                    className={`p-1 rounded transition-colors ${
                      leaveViewMode === 'calendar'
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Calendar view"
                  >
                    <Calendar className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                {leaveLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-1.5"
                  >
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="h-3 bg-gray-200 rounded w-24 mb-0.5 animate-pulse"></div>
                            <div className="h-[11px] bg-gray-200 rounded w-48 animate-pulse"></div>
                          </div>
                          <div className="h-[18px] w-16 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                ) : upcomingLeave.length > 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div>
                      {leaveViewMode === 'list' ? (
                        <div className="space-y-1.5">
                          {upcomingLeave.slice(0, 3).map((leave) => (
                          <div
                            key={leave.id}
                            className="group relative bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-2 hover:border-gray-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-xs text-gray-900 mb-0.5 capitalize">
                                  {leave.leave_type?.replace(/_/g, ' ') || 'Leave'}
                                </div>
                                <div className="text-[11px] text-gray-500 font-normal">
                                  {new Date(leave.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} - {new Date(leave.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              </div>
                              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-md border border-emerald-100 whitespace-nowrap capitalize">
                                {leave.status || 'Approved'}
                              </span>
                            </div>
                          </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                        {/* Calendar Grid */}
                        {(() => {
                          const today = new Date();

                          // Get first day of month and total days
                          const firstDay = new Date(calendarYear, calendarMonth, 1);
                          const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
                          const daysInMonth = lastDay.getDate();
                          const startingDayOfWeek = firstDay.getDay();

                          // Create array of leave dates for quick lookup
                          const leaveDates = new Set();
                          const leaveByDate = {};
                          upcomingLeave.forEach(leave => {
                            const start = new Date(leave.start_date);
                            const end = new Date(leave.end_date);
                            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                              const dateStr = d.toISOString().split('T')[0];
                              leaveDates.add(dateStr);
                              if (!leaveByDate[dateStr]) {
                                leaveByDate[dateStr] = leave;
                              }
                            }
                          });

                          // Build calendar grid
                          const weeks = [];
                          let days = [];

                          // Add empty cells for days before month starts
                          for (let i = 0; i < startingDayOfWeek; i++) {
                            days.push(<div key={`empty-${i}`} className="h-6" />);
                          }

                          // Add days of month
                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(calendarYear, calendarMonth, day);
                            const dateStr = date.toISOString().split('T')[0];
                            const hasLeave = leaveDates.has(dateStr);
                            const isToday = day === today.getDate() && calendarMonth === today.getMonth() && calendarYear === today.getFullYear();
                            const leave = leaveByDate[dateStr];

                            days.push(
                              <div
                                key={day}
                                className={`h-6 flex items-center justify-center text-[10px] rounded relative group ${
                                  hasLeave
                                    ? 'bg-emerald-100 text-emerald-900 font-semibold border border-emerald-300'
                                    : isToday
                                    ? 'bg-blue-50 text-blue-900 font-semibold border border-blue-200'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {day}
                                {hasLeave && leave && (
                                  <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded whitespace-nowrap z-10 pointer-events-none">
                                    {leave.type_info?.description}
                                  </div>
                                )}
                              </div>
                            );

                            if (days.length === 7) {
                              weeks.push(days);
                              days = [];
                            }
                          }

                          // Add remaining days
                          if (days.length > 0) {
                            while (days.length < 7) {
                              days.push(<div key={`empty-end-${days.length}`} className="h-6" />);
                            }
                            weeks.push(days);
                          }

                          return (
                            <div>
                              {/* Month Header with Navigation */}
                              <div className="flex items-center justify-between mb-1.5">
                                <button
                                  onClick={() => {
                                    if (calendarMonth === 0) {
                                      setCalendarMonth(11);
                                      setCalendarYear(calendarYear - 1);
                                    } else {
                                      setCalendarMonth(calendarMonth - 1);
                                    }
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  title="Previous month"
                                >
                                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                                </button>
                                <h4 className="text-xs font-semibold text-gray-900">
                                  {firstDay.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                                </h4>
                                <button
                                  onClick={() => {
                                    if (calendarMonth === 11) {
                                      setCalendarMonth(0);
                                      setCalendarYear(calendarYear + 1);
                                    } else {
                                      setCalendarMonth(calendarMonth + 1);
                                    }
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  title="Next month"
                                >
                                  <ChevronRight className="h-4 w-4 text-gray-600" />
                                </button>
                              </div>

                              {/* Day Headers */}
                              <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                                  <div key={i} className="text-center text-[9px] font-semibold text-gray-500">
                                    {day}
                                  </div>
                                ))}
                              </div>

                              {/* Calendar Grid */}
                              {weeks.map((week, i) => (
                                <div key={i} className="grid grid-cols-7 gap-0.5 mb-0.5">
                                  {week}
                                </div>
                              ))}

                              {/* Legend */}
                              <div className="flex items-center gap-3 mt-2 pt-1.5 border-t text-[9px]">
                                <div className="flex items-center gap-1">
                                  <div className="w-2.5 h-2.5 rounded bg-emerald-100 border border-emerald-300" />
                                  <span className="text-gray-600">Leave</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <div className="w-2.5 h-2.5 rounded bg-blue-50 border border-blue-200" />
                                  <span className="text-gray-600">Today</span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        </div>
                      )}
                    </div>
                    {isOwnProfile && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          className="flex-1 h-8 text-xs font-medium"
                          onClick={() => navigate('/attendance/request')}
                        >
                          Request Leave
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 h-8 text-xs font-medium"
                          onClick={() => navigate('/attendance/history')}
                        >
                          View All
                        </Button>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <div className="text-center py-6">
                    <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">No upcoming leave</p>
                    {isOwnProfile && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 h-8 text-xs"
                        onClick={() => navigate('/attendance/request')}
                      >
                        Request Leave
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Recent Posts Section */}
            <Card className="shadow-sm overflow-hidden p-0 gap-0">
              <div className="py-2.5 px-3.5 border-b bg-gray-50/50 flex items-center justify-between h-11">
                <h3 className="text-sm font-semibold text-gray-900">Recent Posts</h3>
              </div>
              <div className="p-4">
                {postsLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-2"
                  >
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="bg-white border border-gray-200 rounded-lg p-2.5"
                      >
                        <div className="flex items-start gap-2.5">
                          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                                <div className="h-2 w-1 bg-gray-300 rounded"></div>
                                <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
                              </div>
                            </div>
                            <div className="h-3.5 bg-gray-200 rounded w-3/4 mb-1 animate-pulse"></div>
                            <div className="space-y-1">
                              <div className="h-3 bg-gray-200 rounded w-full animate-pulse"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                ) : recentPosts.length > 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-2"
                  >
                    {recentPosts.map((post) => {
                      // Use the userProfile data we already have since all posts are from this user
                      const creatorName = userProfile
                        ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim()
                        : 'Unknown User';
                      const initials = userProfile
                        ? getInitials(userProfile.first_name, userProfile.last_name)
                        : '??';

                      // Determine location display
                      let locationName = '';
                      let locationNames = []; // For multiple spaces
                      if (post.projects?.name) {
                        // Single space post
                        locationName = post.projects.name;
                      } else if (post.selectedProjectNames && post.selectedProjectNames.length > 0) {
                        // Multiple spaces - show all names in tooltip
                        locationNames = post.selectedProjectNames;
                      } else if (post.selected_project_ids?.length > 0) {
                        // Fallback if names couldn't be fetched
                        locationName = 'Multiple Spaces';
                      } else if (!post.project_id && (!post.selected_project_ids || post.selected_project_ids.length === 0)) {
                        // Company-wide post (no project_id and no selected_project_ids)
                        locationName = 'All Spaces';
                      }

                      // Strip HTML tags from content for preview
                      const textContent = post.content
                        ? post.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
                        : '';

                      const handlePostClick = () => {
                        // Navigate based on post type
                        if (post.project_id) {
                          // Single project post
                          navigate(`/space/${post.project_id}/message-board/${post.id}`);
                        } else if (post.selected_project_ids && post.selected_project_ids.length > 0) {
                          // Multiple spaces selected
                          if (post.selected_project_ids.length === 1) {
                            // Only one space - navigate to that space
                            navigate(`/space/${post.selected_project_ids[0]}/message-board/${post.id}`);
                          } else {
                            // More than one space - navigate to global posts
                            navigate(`/posts/${post.id}`);
                          }
                        } else if (post.company_id) {
                          // Company-wide post - navigate to global posts page
                          navigate(`/posts/${post.id}`);
                        }
                      };

                      return (
                        <div
                          key={post.id}
                          onClick={handlePostClick}
                          className="group relative bg-white border border-gray-200 rounded-lg p-2.5 hover:border-gray-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                        >
                          <div className="flex items-start gap-2.5">
                            {/* Avatar */}
                            {userProfile?.profile_image_url ? (
                              <img
                                src={userProfile.profile_image_url}
                                alt={creatorName}
                                className="flex-shrink-0 w-8 h-8 rounded-full object-cover shadow-sm"
                              />
                            ) : (
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                                {initials}
                              </div>
                            )}

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* Header Row */}
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {locationNames.length > 0 ? (
                                    // Multiple spaces - show count with tooltip
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-700 text-[10px] font-medium rounded-md border border-gray-200 cursor-pointer">
                                            <Building2 className="h-3 w-3 text-gray-500" />
                                            {locationNames.length} Space{locationNames.length !== 1 ? 's' : ''}
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <div className="text-xs">
                                            {locationNames.map((name, idx) => (
                                              <div key={idx}>• {name}</div>
                                            ))}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  ) : locationName ? (
                                    // Single location or company-wide
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 text-gray-700 text-[10px] font-medium rounded-md border border-gray-200">
                                      <Building2 className="h-3 w-3 text-gray-500" />
                                      {locationName}
                                    </span>
                                  ) : null}
                                  <span className="text-[11px] text-gray-500">{formatTimeAgo(post.created_date)}</span>
                                  {post.requires_understanding && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-md border border-amber-200">
                                      <CheckCircle className="h-3 w-3" />
                                      Requires Understanding
                                    </span>
                                  )}
                                  {post.requires_reading && !post.requires_understanding && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded-md border border-blue-200">
                                      <CheckCircle className="h-3 w-3" />
                                      Requires Reading
                                    </span>
                                  )}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 -mt-0.5 -mr-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <MoreVertical className="h-3.5 w-3.5 text-gray-400" />
                                </Button>
                              </div>

                              {/* Title */}
                              <h4 className="font-semibold text-sm text-gray-900 mb-0.5 line-clamp-1">
                                {post.title || 'Untitled Post'}
                              </h4>

                              {/* Preview Text */}
                              {textContent && (
                                <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                                  {textContent}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </motion.div>
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[240px] text-center py-8">
                    <div className="text-gray-400 mb-2">
                      <MessageSquare className="h-8 w-8 mx-auto" />
                    </div>
                    <p className="text-sm text-gray-600 font-medium">No posts yet</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Posts created by this user will appear here
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
              </TabsContent>

              {/* Qualifications Tab */}
              <TabsContent value="qualifications" className="mt-0 p-0">
          {/* Qualifications Section - Show manager UI for admins/managers viewing another user */}
          <div className="mb-8">
            {!isOwnProfile && (userRole === 'Admin' || userRole === 'Manager') ? (
              /* Admin/Manager viewing another user - show management UI */
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ProfileQualificationsManager
                  userId={userProfile?.id}
                  userProfile={userProfile}
                />
              </motion.div>
            ) : qualificationsLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Card>
                  <CardContent className="py-8">
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                          <div className="h-10 w-10 bg-gray-200 rounded animate-pulse"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-3 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                            <div className="h-2 bg-gray-200 rounded w-1/4 animate-pulse"></div>
                          </div>
                          <div className="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ) : qualificationsData.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <QualificationsTable
                  data={qualificationsData}
                  onUploadSuccess={() => loadQualifications(userProfile.id)}
                  onManualUploadConfirm={handleManualUploadConfirm}
                />
              </motion.div>
            ) : (
              <Card>
                <CardContent className="py-8">
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    <FileText className="h-12 w-12 mb-3 opacity-50" />
                    <p>No qualifications assigned yet</p>
                    <p className="text-sm mt-1">Qualifications will appear here once assigned in project matrices</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="mt-0 p-0">
                <ProfileDocuments
                  documents={documents}
                  documentsLoading={documentsLoading}
                  userProfile={userProfile}
                  company={company}
                  currentUserProfile={currentUserProfile}
                  userRole={userRole}
                  isOwnProfile={isOwnProfile}
                  onDocumentsChange={() => loadDocuments(userProfile?.id)}
                />
              </TabsContent>

              {/* Spaces Tab */}
              <TabsContent value="spaces" className="mt-0 p-0">
          {/* My Spaces Section */}
          <div className="mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex-1">
                  <CardTitle>My Spaces</CardTitle>
                </div>
                {userSpaces.length > 0 && (
                  <div className="flex gap-2 items-center">
                    {/* Search Box */}
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search spaces..."
                        value={spacesSearchQuery}
                        onChange={(e) => setSpacesSearchQuery(e.target.value)}
                        className="pl-9 h-9"
                      />
                    </div>
                    {/* Navigation Buttons */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (spacesScrollRef.current) {
                          spacesScrollRef.current.scrollBy({ left: -400, behavior: 'smooth' });
                        }
                      }}
                    >
                      <MoveLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (spacesScrollRef.current) {
                          spacesScrollRef.current.scrollBy({ left: 400, behavior: 'smooth' });
                        }
                      }}
                    >
                      <MoveRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {userSpaces.length > 0 ? (
                  <div
                    ref={spacesScrollRef}
                    className="flex gap-4 overflow-x-scroll pb-4 scroll-smooth"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#cbd5e0 #f1f5f9',
                      scrollbarGutter: 'stable'
                    }}
                  >
                    {userSpaces
                      .filter(space => {
                        if (!spacesSearchQuery.trim()) return true;
                        const query = spacesSearchQuery.toLowerCase();
                        return (
                          space.project.name?.toLowerCase().includes(query) ||
                          space.project.description?.toLowerCase().includes(query) ||
                          space.project.location?.toLowerCase().includes(query) ||
                          space.project.type?.toLowerCase().includes(query)
                        );
                      })
                      .map((space, i) => {
                        // Handle color - it could be a string or an object
                        let colorValue = '#FFFFFF';
                        if (space.project.color) {
                          if (typeof space.project.color === 'string') {
                            colorValue = space.project.color;
                          } else if (space.project.color.value) {
                            colorValue = space.project.color.value;
                          }
                        }

                        // Team members are already in the correct format from the query
                        const teamMembers = space.project.teamMembers || [];

                        return (
                          <div key={space.project.id || i} className="flex-shrink-0 w-[280px]">
                            <ProjectCard
                              project={{
                                id: space.project.id,
                                name: space.project.name,
                                type: space.project.type,
                                description: space.project.description,
                                location: space.project.location,
                                avatar: space.project.avatar,
                                color: { value: colorValue },
                                teamMembers: teamMembers,
                                toolCount: space.project.toolCount || 0,
                                complianceSummary: space.project.complianceSummary
                              }}
                              isDragActive={false}
                              isDropTarget={false}
                            />
                          </div>
                        );
                      })}
                    {/* Show "no results" message if search returns nothing */}
                    {userSpaces.filter(space => {
                      if (!spacesSearchQuery.trim()) return true;
                      const query = spacesSearchQuery.toLowerCase();
                      return (
                        space.project.name?.toLowerCase().includes(query) ||
                        space.project.description?.toLowerCase().includes(query) ||
                        space.project.location?.toLowerCase().includes(query) ||
                        space.project.type?.toLowerCase().includes(query)
                      );
                    }).length === 0 && spacesSearchQuery && (
                      <div className="flex-1 flex items-center justify-center py-12 text-gray-500">
                        <div className="text-center">
                          <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">No spaces found matching "{spacesSearchQuery}"</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-600">You haven't joined any spaces yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
              </TabsContent>

              {/* Leave Tab */}
              <TabsContent value="leave" className="mt-0 p-0">
                <div className="space-y-4">
                  {/* Leave Balance Stats */}
                  {leaveLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 animate-pulse">
                          <div className="p-2 rounded-xl bg-white/80">
                            <div className="h-4 w-4 bg-gray-200 rounded"></div>
                          </div>
                          <div className="space-y-1">
                            <div className="h-3 bg-gray-200 rounded w-12"></div>
                            <div className="h-5 bg-gray-200 rounded w-6"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : leaveBalance && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-indigo-50 text-indigo-700 border-indigo-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default">
                        <div className="p-2 rounded-xl bg-white/80 shadow-sm">
                          <Sun className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium opacity-70">Entitled</p>
                          <p className="text-lg font-bold -mt-0.5">{leaveBalance.entitled_days || 0}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-amber-50 text-amber-700 border-amber-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default">
                        <div className="p-2 rounded-xl bg-white/80 shadow-sm">
                          <Palmtree className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium opacity-70">Taken</p>
                          <p className="text-lg font-bold -mt-0.5">{leaveBalance.taken_days || 0}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-emerald-50 text-emerald-700 border-emerald-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default">
                        <div className="p-2 rounded-xl bg-white/80 shadow-sm">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium opacity-70">Remaining</p>
                          <p className="text-lg font-bold -mt-0.5">{leaveBalance.remaining_days || 0}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-violet-50 text-violet-700 border-violet-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default">
                        <div className="p-2 rounded-xl bg-white/80 shadow-sm">
                          <Clock className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium opacity-70">Pending</p>
                          <p className="text-lg font-bold -mt-0.5">{leaveHistory.filter(l => l.status === 'pending').length}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-slate-50 text-slate-700 border-slate-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default">
                        <div className="p-2 rounded-xl bg-white/80 shadow-sm">
                          <CalendarCheck className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium opacity-70">Approved</p>
                          <p className="text-lg font-bold -mt-0.5">{leaveHistory.filter(l => l.status === 'approved').length}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border bg-slate-50 text-slate-700 border-slate-200 transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default">
                        <div className="p-2 rounded-xl bg-white/80 shadow-sm">
                          <Calendar className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium opacity-70">Total</p>
                          <p className="text-lg font-bold -mt-0.5">{leaveHistory.length}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Leave History */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle>Leave History</CardTitle>
                        <CardDescription>View all leave requests and history</CardDescription>
                      </div>
                      {isOwnProfile && (
                        <Button
                          variant="outline"
                          onClick={() => navigate('/attendance')}
                          className="gap-2"
                        >
                          <CalendarDays className="h-4 w-4" />
                          Go to Attendance
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {leaveLoading ? (
                        <div className="space-y-3">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg animate-pulse">
                              <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                              <div className="flex-1 space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                              </div>
                              <div className="h-6 w-20 bg-gray-200 rounded"></div>
                            </div>
                          ))}
                        </div>
                      ) : leaveHistory.length > 0 ? (
                        <div className="space-y-3">
                          {leaveHistory.map((leave) => {
                            const startDate = new Date(leave.start_date);
                            const endDate = new Date(leave.end_date);
                            const days = leave.calculated_days || Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

                            return (
                              <div
                                key={leave.id}
                                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Calendar className="h-5 w-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 capitalize">
                                    {leave.leave_type?.replace(/_/g, ' ') || 'Leave'}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    {days > 1 && ` - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                    <span className="text-gray-400 ml-2">({days} day{days !== 1 ? 's' : ''})</span>
                                  </p>
                                </div>
                                <Badge
                                  variant="outline"
                                  className={
                                    leave.status === 'approved'
                                      ? 'bg-green-50 text-green-700 border-green-200'
                                      : leave.status === 'pending'
                                      ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                      : leave.status === 'rejected'
                                      ? 'bg-red-50 text-red-700 border-red-200'
                                      : 'bg-gray-50 text-gray-700 border-gray-200'
                                  }
                                >
                                  {leave.status ? leave.status.charAt(0).toUpperCase() + leave.status.slice(1) : 'Pending'}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-600">No leave records found</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {isOwnProfile ? 'Your leave requests will appear here' : 'Leave records will appear here when available'}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Custom Profile Tabs (iframe embeds) */}
              {customProfileTabs.map(ct => {
                const tabValue = `custom_${ct.id}`;
                const resolvedUrl = resolveTabUrl(ct.url, userProfile, company);
                const isTabLoading = customTabLoadingStates[ct.id] !== false;
                return (
                  <TabsContent key={tabValue} value={tabValue} className="mt-0 p-0">
                    <div className="relative w-full" style={{ minHeight: '600px' }}>
                      {/* Loading overlay */}
                      {isTabLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-b-lg">
                          <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Loading {ct.name}...</p>
                          </div>
                        </div>
                      )}
                      <iframe
                        src={resolvedUrl}
                        title={ct.name}
                        className="w-full border-0 rounded-b-lg"
                        style={{ height: '600px' }}
                        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                        allow="clipboard-write; clipboard-read"
                        referrerPolicy="strict-origin-when-cross-origin"
                        onLoad={() => setCustomTabLoadingStates(prev => ({ ...prev, [ct.id]: false }))}
                        onError={() => setCustomTabLoadingStates(prev => ({ ...prev, [ct.id]: false }))}
                      />
                      {/* Toolbar */}
                      <div className="flex items-center justify-end gap-2 pt-2 px-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground"
                          onClick={() => {
                            setCustomTabLoadingStates(prev => ({ ...prev, [ct.id]: true }));
                            // Force re-render by toggling tab
                            setActiveTab('overview');
                            setTimeout(() => setActiveTab(tabValue), 50);
                          }}
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Refresh
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground"
                          onClick={() => window.open(resolvedUrl, '_blank', 'noopener,noreferrer')}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Open in New Tab
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                );
              })}
              </div>
              </div>
            </Tabs>
          </div>
        )}

          {/* Schedule & Training Section */}
          {/* <div className="mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle>Schedule & Training</CardTitle>
                  <CardDescription className="mt-1">{formatWeekRange(currentWeekStart)}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousWeek}
                  >
                    <MoveLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextWeek}
                  >
                    <MoveRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {scheduleLoading && !scheduleData ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="grid grid-cols-7 gap-2"
                  >
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <div
                        key={i}
                        className="rounded-lg border border-gray-200 p-3 text-center"
                      >
                        <div className="mb-2">
                          <div className="h-3 bg-gray-200 rounded w-12 mx-auto mb-1 animate-pulse"></div>
                          <div className="h-6 bg-gray-200 rounded w-8 mx-auto animate-pulse"></div>
                        </div>
                        <div className="h-16 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </motion.div>
                ) : scheduleData && scheduleData.length > 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="grid grid-cols-7 gap-2"
                  >
                    {scheduleData.map((day, i) => {
                      // Determine what to display - leave overrides shift
                      const displayLeave = day.leave;
                      const displayShift = !displayLeave && day.shift;

                      const ShiftIcon = displayShift ? getShiftIcon(day.shift.status) : null;

                      return (
                        <div
                          key={i}
                          className={`rounded-lg border p-3 text-center transition-all ${
                            displayLeave
                              ? 'bg-blue-50 border-blue-200'
                              : day.isWeekend
                              ? 'bg-gray-50 border-gray-200'
                              : 'bg-white border-gray-200 hover:border-violet-300'
                          }`}
                        >
                          {/* Day Name & Date */}
                          {/* <div className="mb-2">
                            <div className="text-xs font-semibold text-gray-600">
                              {day.dayName}
                            </div>
                            <div className="text-lg font-bold text-gray-900">
                              {day.dayNumber}
                            </div>
                          </div>

                          {/* Icon - Leave overrides shift */}
                          {/* {displayLeave ? (
                            <div className="flex justify-center mb-2">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Calendar className="h-4 w-4 text-blue-600" />
                              </div>
                            </div>
                          ) : ShiftIcon ? (
                            <div className="flex justify-center mb-2">
                              <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                                <ShiftIcon className="h-4 w-4 text-violet-600" />
                              </div>
                            </div>
                          ) : (
                            <div className="h-8 mb-2" />
                          )}

                          {/* Time Range */}
                          {/* {displayLeave ? (
                            <div className="text-xs font-medium text-blue-900 mb-1">
                              {getLeaveTypeLabel(day.leave.type)}
                            </div>
                          ) : displayShift && day.shift.startTime && day.shift.endTime ? (
                            <div className="text-xs font-medium text-gray-900 mb-1">
                              {day.shift.startTime.substring(0, 5)} - {day.shift.endTime.substring(0, 5)}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 mb-1">Off</div>
                          )}

                          {/* Location */}
                          {/* {displayLeave ? (
                            day.leave.isPartDay ? (
                              <div className="text-[10px] text-blue-600">
                                {day.leave.partDayPeriod === 'am' ? 'Morning' : 'Afternoon'}
                              </div>
                            ) : (
                              <div className="text-[10px] text-blue-600">All Day</div>
                            )
                          ) : displayShift && day.shift.location ? (
                            <div className="text-[10px] text-gray-600 truncate">
                              {day.shift.location}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </motion.div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No schedule data available</p>
                    <p className="text-xs mt-1">Schedule information will appear here once set up</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div> */}

          {/* Achievements Section - TODO: Post-MVP */}
          {/* <div className="mb-8">
            <Card>
              <CardHeader>
                <CardTitle>Achievements</CardTitle>
                <CardDescription>Badges and milestones you've earned</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {[
                    { name: "Quick Learner", icon: "🚀", color: "bg-blue-100", description: "Complete 5 modules in a week" },
                    { name: "Team Player", icon: "🤝", color: "bg-green-100", description: "Collaborate on 10 projects" },
                    { name: "Safety First", icon: "🛡️", color: "bg-yellow-100", description: "Perfect safety record" },
                    { name: "Problem Solver", icon: "💡", color: "bg-purple-100", description: "Resolve 20 issues" },
                    { name: "Early Bird", icon: "🌅", color: "bg-orange-100", description: "First to complete training" },
                    { name: "Mentor", icon: "🎓", color: "bg-indigo-100", description: "Train 5 team members" }
                  ].map((achievement, i) => (
                    <div key={i} className="text-center group cursor-pointer">
                      <div className={`${achievement.color} rounded-lg p-4 mb-2 group-hover:scale-105 transition-transform`}>
                        <span className="text-2xl">{achievement.icon}</span>
                      </div>
                      <p className="text-xs font-medium">{achievement.name}</p>
                      <p className="text-xs text-gray-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">{achievement.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div> */}
        </PageTransition>
      </div>
    </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>User QR Code</DialogTitle>
            <DialogDescription>
              Scan this code to quickly access {isOwnProfile ? 'your' : `${getDisplayName()}'s`} profile
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="bg-white rounded-lg border-2 border-gray-200 p-4 shadow-sm">
              <QRCodeSVG
                id="user-qr-code"
                value={`${window.location.origin}/profile/${userProfile?.id || ''}`}
                size={200}
                level="H"
                includeMargin={false}
              />
            </div>
            <Button
              variant="outline"
              onClick={downloadQRCode}
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              Download QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Emergency Contact Dialog */}
      <Dialog open={showEmergencyContactDialog} onOpenChange={setShowEmergencyContactDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Emergency Contact</DialogTitle>
            <DialogDescription>
              {isOwnProfile ? 'Your emergency contact information' : `Contact details for ${getDisplayName()}`}
            </DialogDescription>
          </DialogHeader>

          {userProfile?.emergency_contact_name || userProfile?.emergency_contact_mobile_phone ? (
            <div className="space-y-6 mt-4">
              {/* Contact Name & Relationship */}
              {userProfile?.emergency_contact_name && (
                <div>
                  <Label className="text-xs text-gray-500">Contact Name</Label>
                  <p className="text-sm font-medium mt-1">{userProfile.emergency_contact_name}</p>
                  {userProfile?.emergency_contact_relationship && (
                    <p className="text-xs text-gray-500 mt-1">{userProfile.emergency_contact_relationship}</p>
                  )}
                </div>
              )}

              <Separator />

              {/* Contact Methods */}
              <div className="space-y-3">
                {userProfile?.emergency_contact_mobile_phone && (
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-gray-500">Mobile</Label>
                      <p className="text-sm font-medium mt-1">{userProfile.emergency_contact_mobile_phone}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = `tel:${userProfile.emergency_contact_mobile_phone}`}
                    >
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {userProfile?.emergency_contact_home_phone && (
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-gray-500">Home</Label>
                      <p className="text-sm font-medium mt-1">{userProfile.emergency_contact_home_phone}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = `tel:${userProfile.emergency_contact_home_phone}`}
                    >
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {userProfile?.emergency_contact_work_phone && (
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-gray-500">Work</Label>
                      <p className="text-sm font-medium mt-1">{userProfile.emergency_contact_work_phone}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = `tel:${userProfile.emergency_contact_work_phone}`}
                    >
                      <Phone className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {userProfile?.emergency_contact_email && (
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      <p className="text-sm font-medium mt-1">{userProfile.emergency_contact_email}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = `mailto:${userProfile.emergency_contact_email}`}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Additional Information */}
              {(userProfile?.emergency_contact_home_address || userProfile?.emergency_contact_employer) && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    {userProfile?.emergency_contact_home_address && (
                      <div>
                        <Label className="text-xs text-gray-500">Address</Label>
                        <div className="flex items-center justify-between gap-3 mt-1">
                          <p className="text-sm flex-1 min-w-0">{userProfile.emergency_contact_home_address}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-shrink-0"
                            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(userProfile.emergency_contact_home_address)}`, '_blank')}
                          >
                            <MapPin className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {userProfile?.emergency_contact_employer && (
                      <div>
                        <Label className="text-xs text-gray-500">Employer</Label>
                        <p className="text-sm mt-1">{userProfile.emergency_contact_employer}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-4">
                {isOwnProfile
                  ? 'No emergency contact information added'
                  : 'This user has not added an emergency contact'}
              </p>
              {isOwnProfile ? (
                <Link to="/settings">
                  <Button size="sm" variant="outline">
                    <Settings className="h-4 w-4 mr-2" />
                    Add Contact
                  </Button>
                </Link>
              ) : (
                <Button size="sm" variant="outline" onClick={sendEmergencyContactReminder}>
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Request Details from {userProfile?.first_name}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drag-and-drop Overlay */}
      <AnimatePresence>
        {isDraggingFiles && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-blue-500/20 backdrop-blur-sm pointer-events-none flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md border-4 border-blue-500 border-dashed"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Drop Certificate Here
                </h3>
                <p className="text-gray-600">
                  AI will automatically match it to the correct qualification
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Smart Upload Processing Overlay */}
      <AnimatePresence>
        {smartUploadProcessing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center"
            >
              <div className="relative mb-6">
                <div className="w-16 h-16 mx-auto border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                <Sparkles className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                AI is analyzing your certificate
              </h3>
              <p className="text-gray-600">
                Reading the document to find the qualification...
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual Upload Confirmation Dialog */}
      <Dialog open={!!manualUploadConfirmation} onOpenChange={(open) => !open && handleDeclineManualUpload()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {manualUploadConfirmation?.warnings?.length > 0 ? (
                <>
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Confirm Certificate Upload - Warnings Found
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Confirm Certificate Upload
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {manualUploadConfirmation?.warnings?.length > 0
                ? 'AI has found some potential issues. Please review before confirming.'
                : 'AI has analyzed the certificate. Please review and confirm.'}
            </DialogDescription>
          </DialogHeader>

          {manualUploadConfirmation && (
            <div className="space-y-4 py-4">
              {/* Warnings Section */}
              {manualUploadConfirmation.warnings?.length > 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 text-sm mb-2">Validation Warnings</h4>
                      <ul className="space-y-1">
                        {manualUploadConfirmation.warnings.map((warning, idx) => (
                          <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                            <span className="text-amber-600">•</span>
                            <span>{warning.message}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    You can still proceed if you're confident this is the correct certificate.
                  </p>
                </div>
              )}

              {/* Match Details */}
              <div className={manualUploadConfirmation.warnings?.length > 0 ? 'bg-amber-50 border border-amber-200 rounded-lg p-4' : 'bg-green-50 border border-green-200 rounded-lg p-4'}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 bg-gradient-to-br rounded-xl flex items-center justify-center text-sm font-medium ${
                    manualUploadConfirmation.warnings?.length > 0
                      ? 'from-amber-100 to-amber-200 text-amber-700'
                      : 'from-green-100 to-green-200 text-green-700'
                  }`}>
                    {getDisplayName().split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${
                      manualUploadConfirmation.warnings?.length > 0 ? 'text-amber-900' : 'text-green-900'
                    }`}>
                      {getDisplayName()}
                    </p>
                    <p className={`text-xs ${
                      manualUploadConfirmation.warnings?.length > 0 ? 'text-amber-700' : 'text-green-700'
                    }`}>
                      User
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-center my-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    manualUploadConfirmation.warnings?.length > 0 ? 'bg-amber-600' : 'bg-green-600'
                  }`}>
                    <ArrowDown className="h-4 w-4 text-white" />
                  </div>
                </div>

                <div className={`bg-white rounded-lg p-3 border ${
                  manualUploadConfirmation.warnings?.length > 0 ? 'border-amber-200' : 'border-green-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Award className={`h-4 w-4 ${
                      manualUploadConfirmation.warnings?.length > 0 ? 'text-amber-600' : 'text-green-600'
                    }`} />
                    <p className={`text-xs font-semibold ${
                      manualUploadConfirmation.warnings?.length > 0 ? 'text-amber-900' : 'text-green-900'
                    }`}>
                      Qualification
                    </p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {manualUploadConfirmation.qualification.requirement_name}
                  </p>
                </div>
              </div>

              {/* Extracted Information */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">Extracted Information</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">File:</span>
                    <span className="font-medium text-gray-900">{manualUploadConfirmation.file.name}</span>
                  </div>
                  {manualUploadConfirmation.extractedData.firstName && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Certificate Name:</span>
                      <span className="font-medium text-gray-900">
                        {manualUploadConfirmation.extractedData.firstName} {manualUploadConfirmation.extractedData.lastName}
                      </span>
                    </div>
                  )}
                  {manualUploadConfirmation.extractedData.courseTitle && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Course Title:</span>
                      <span className="font-medium text-gray-900">{manualUploadConfirmation.extractedData.courseTitle}</span>
                    </div>
                  )}
                  {manualUploadConfirmation.extractedData.expiryDate && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Expiry Date:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(manualUploadConfirmation.extractedData.expiryDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {manualUploadConfirmation.extractedData.certificateNumber && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Certificate Number:</span>
                      <span className="font-medium text-gray-900">{manualUploadConfirmation.extractedData.certificateNumber}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDeclineManualUpload}
                >
                  Cancel
                </Button>
                <Button
                  className={`flex-1 ${
                    manualUploadConfirmation.warnings?.length > 0
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                  onClick={handleConfirmManualUpload}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {manualUploadConfirmation.warnings?.length > 0 ? 'Proceed Anyway' : 'Confirm & Save'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Smart Upload Confirmation Dialog */}
      <Dialog open={!!smartUploadConfirmation} onOpenChange={(open) => !open && handleDeclineSmartUpload()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Confirm Certificate Match
            </DialogTitle>
            <DialogDescription>
              AI has found a match for this certificate. Please review and confirm.
            </DialogDescription>
          </DialogHeader>

          {smartUploadConfirmation && (
            <div className="space-y-4 py-4">
              {/* Match Details */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-green-200 rounded-xl flex items-center justify-center text-green-700 text-sm font-medium">
                    {getDisplayName().split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-900">
                      {getDisplayName()}
                    </p>
                    <p className="text-xs text-green-700">User</p>
                  </div>
                </div>

                <div className="flex items-center justify-center my-2">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
                    <ArrowDown className="h-4 w-4 text-white" />
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="h-4 w-4 text-green-600" />
                    <p className="text-xs font-semibold text-green-900">Qualification</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {smartUploadConfirmation.qualification.requirement_name}
                  </p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">File:</span>
                  <span className="font-medium text-gray-900">{smartUploadConfirmation.fileName}</span>
                </div>
                {smartUploadConfirmation.expiryDate && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Expiry Date:</span>
                    <span className="font-medium text-gray-900">
                      {new Date(smartUploadConfirmation.expiryDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {smartUploadConfirmation.certificateNumber && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Certificate Number:</span>
                    <span className="font-medium text-gray-900">{smartUploadConfirmation.certificateNumber}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDeclineSmartUpload}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleConfirmSmartUpload}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirm & Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send SMS Dialog */}
      {userProfile && (
        <SendSmsDialog
          open={showSmsDialog}
          onOpenChange={setShowSmsDialog}
          recipient={userProfile}
          company={company}
        />
      )}

    </AppShell3>
  );
}